import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useCart } from './CartContext'; // Impor useCart

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    
    // Dapatkan akses ke fungsi fetchCart dari CartContext
    // Pastikan AuthProvider dibungkus di dalam CartProvider di index.js atau App.js
    const cartContext = useCart();

    // Fungsi fetchUser dipisahkan agar bisa dipanggil ulang
    const fetchUser = async () => {
        try {
            const { data } = await api.get('/users/me');
            setUser(data);
        } catch (err) {
            setUser(null);
        }
    };

    useEffect(() => {
        const checkUserStatus = async () => {
            await fetchUser();
            setLoading(false);
        };
        checkUserStatus();
    }, []);

    const login = async (credentials) => {
        try {
            // Langkah 1: Panggil endpoint login. Backend akan mengirim httpOnly cookie.
            await api.post('/auth/login', credentials);
            
            // Langkah 2: Panggil fetchUser() untuk mengambil data user.
            // Ini memastikan cookie sudah terpasang sebelum data diminta.
            await fetchUser();

            // Langkah 3 (Sangat Penting): Gabungkan keranjang guest
            const localCart = JSON.parse(localStorage.getItem('cart')) || [];
            if (localCart.length > 0) {
                await api.post('/cart/merge', { localItems: localCart });
                localStorage.removeItem('cart');
            }

            // Langkah 4: Refresh state keranjang dengan data dari server
            if (cartContext) {
                await cartContext.fetchCart();
            }

            navigate('/');
            
        } catch (err) {
            // Lempar error agar LoginPage bisa menampilkannya di UI
            throw err;
        }
    };

    const register = async (userData) => {
        try {
            const response = await api.post('/auth/register-customer', userData);
            return { success: true, data: response.data };
        } catch (err) {
            return { success: false, message: err.response?.data?.message || 'Registrasi gagal.' };
        }
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (err) {
            // Abaikan error jika logout gagal (misal, token sudah expired)
        } finally {
            setUser(null);
            // Hapus juga keranjang guest jika ada saat logout
            localStorage.removeItem('cart'); 
            navigate('/login');
            toast.info("Anda telah logout.");
        }
    };

    const updateUser = (updatedUserData) => {
        setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
    };

    return (
        <AuthContext.Provider value={{ user, setUser, updateUser, isAuthenticated: !!user, loading, login, register, logout }}>
            {/* Jangan render aplikasi sampai pengecekan awal selesai */}
            {!loading && children}
        </AuthContext.Provider>
    );
};